import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandPanel from "../components/BrandPanel";
import LangToggle from "../components/LangToggle";
import { ShieldCheck, Mail, Building2, Smartphone, Lock, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Forgot Password / Account Recovery — 3-step wizard.
 *
 *  Step 1 (identify): company_name + email → /auth/recover/start → recover_init token
 *  Step 2 (totp):     6-digit code from authenticator → /auth/recover/verify-totp → password_reset token
 *  Step 3 (set):      new_password → /auth/recover/set-password → done → /login
 *
 * No QR code anywhere — TOTP is treated purely as a verification challenge.
 */
export default function AccountRecovery() {
    const navigate = useNavigate();
    const { t, lang, toggle } = useLang();

    const [step, setStep] = useState(1);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Step 1
    const [companyName, setCompanyName] = useState("");
    const [email, setEmail] = useState("");

    // After step 1 + 2 we carry these forward
    const [resetToken, setResetToken] = useState("");

    // Step 2
    const [code, setCode] = useState("");

    // Step 3
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");

    const iconSide = lang === "ar" ? { right: "0.75rem" } : { left: "0.75rem" };
    const inputPad = lang === "ar" ? "h-11 pe-10 ps-3" : "h-11 ps-10 pe-3";

    const submitStep1 = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!companyName.trim() || !email.trim()) {
            setError(t.recoverFieldsRequired);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/recover/start", {
                company_name: companyName.trim(),
                email: email.trim(),
            });
            setResetToken(data.reset_token);
            setStep(2);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const submitStep2 = async (e) => {
        e?.preventDefault?.();
        setError("");
        const clean = (code || "").replace(/\D/g, "");
        if (clean.length !== 6) {
            setError(t.totpInvalidFormat);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/recover/verify-totp", {
                reset_token: resetToken,
                code: clean,
            });
            setResetToken(data.reset_token);
            setStep(3);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const submitStep3 = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (newPassword.length < 4) {
            setError(t.passwordTooShort);
            return;
        }
        if (newPassword !== confirm) {
            setError(t.passwordMismatch);
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/recover/set-password", {
                reset_token: resetToken,
                new_password: newPassword,
            });
            toast.success(t.recoverPasswordChangedToast);
            navigate("/login", { replace: true });
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="relative min-h-screen w-full app-bg" dir={t.dir}>
            <LangToggle lang={lang} onToggle={toggle} t={t} />
            <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-2">
                <div className="relative hidden p-6 lg:block">
                    <BrandPanel lang={lang} strings={t} />
                </div>
                <div className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-12">
                    <div className="w-full max-w-md fade-up">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-600 text-white">
                                <ShieldCheck className="h-5 w-5" />
                            </div>
                            <div>
                                <h1 className="font-heading text-xl font-bold sm:text-2xl">{t.recoverTitle}</h1>
                                <p className="text-xs text-neutral-500 sm:text-sm">{t.recoverSubtitle}</p>
                            </div>
                        </div>

                        {/* Step indicator */}
                        <ol className="mb-4 flex items-center gap-2 text-xs">
                            {[1, 2, 3].map((n) => (
                                <li key={n} className="flex flex-1 items-center gap-2">
                                    <span
                                        className={`flex h-6 w-6 items-center justify-center rounded-full font-bold ${
                                            step > n
                                                ? "bg-emerald-600 text-white"
                                                : step === n
                                                    ? "bg-amber-600 text-white"
                                                    : "bg-neutral-200 text-neutral-500"
                                        }`}
                                    >
                                        {step > n ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
                                    </span>
                                    <span className={`flex-1 text-[11px] ${step === n ? "font-bold text-neutral-800" : "text-neutral-500"}`}>
                                        {n === 1 ? t.recoverStep1Label : n === 2 ? t.recoverStep2Label : t.recoverStep3Label}
                                    </span>
                                </li>
                            ))}
                        </ol>

                        {step === 1 ? (
                            <form onSubmit={submitStep1} className="space-y-4" data-testid="recover-step1-form">
                                <p className="text-sm text-neutral-600">{t.recoverStep1Hint}</p>
                                <div>
                                    <Label className="mb-1.5 block text-sm font-medium">{t.companyLabel}</Label>
                                    <div className="relative">
                                        <Building2 className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                        <Input
                                            value={companyName}
                                            onChange={(e) => setCompanyName(e.target.value)}
                                            className={inputPad}
                                            placeholder={t.companyPlaceholder}
                                            required
                                            data-testid="recover-company-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="mb-1.5 block text-sm font-medium">{t.emailLabel}</Label>
                                    <div className="relative">
                                        <Mail className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                        <Input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className={`${inputPad} ltr-text`}
                                            dir="ltr"
                                            placeholder="you@example.com"
                                            required
                                            data-testid="recover-email-input"
                                        />
                                    </div>
                                </div>
                                {error ? (
                                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="recover-error">
                                        {error}
                                    </div>
                                ) : null}
                                <Button
                                    type="submit"
                                    disabled={busy}
                                    className="h-11 w-full bg-amber-700 text-white hover:bg-amber-800"
                                    data-testid="recover-step1-submit"
                                >
                                    {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                                    {t.recoverContinueBtn}
                                </Button>
                            </form>
                        ) : step === 2 ? (
                            <form onSubmit={submitStep2} className="space-y-4" data-testid="recover-step2-form">
                                <p className="text-sm text-neutral-600">{t.recoverStep2Hint}</p>
                                <div>
                                    <Label className="mb-1.5 block text-sm font-medium">{t.totpCodeLabel}</Label>
                                    <div className="relative">
                                        <Smartphone className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                        <Input
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                            className={`${inputPad} font-numbers tracking-[0.4em] text-center text-lg`}
                                            placeholder="••••••"
                                            inputMode="numeric"
                                            autoFocus
                                            data-testid="recover-code-input"
                                        />
                                    </div>
                                    <p className="mt-1.5 text-xs text-neutral-500">{t.totpCodeHint}</p>
                                </div>
                                {error ? (
                                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="recover-error">
                                        {error}
                                    </div>
                                ) : null}
                                <Button
                                    type="submit"
                                    disabled={busy}
                                    className="h-11 w-full bg-amber-700 text-white hover:bg-amber-800"
                                    data-testid="recover-step2-submit"
                                >
                                    {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                                    {t.recoverVerifyBtn}
                                </Button>
                            </form>
                        ) : (
                            <form onSubmit={submitStep3} className="space-y-4" data-testid="recover-step3-form">
                                <p className="text-sm text-neutral-600">{t.recoverStep3Hint}</p>
                                <div>
                                    <Label className="mb-1.5 block text-sm font-medium">{t.recoverNewPasswordLabel}</Label>
                                    <div className="relative">
                                        <Lock className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                        <Input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className={inputPad}
                                            required
                                            data-testid="recover-new-password-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="mb-1.5 block text-sm font-medium">{t.confirmPasswordLabel}</Label>
                                    <Input
                                        type="password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className={lang === "ar" ? "h-11 ps-3" : "h-11 pe-3"}
                                        required
                                        data-testid="recover-confirm-password-input"
                                    />
                                </div>
                                {error ? (
                                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="recover-error">
                                        {error}
                                    </div>
                                ) : null}
                                <Button
                                    type="submit"
                                    disabled={busy}
                                    className="h-11 w-full bg-emerald-700 text-white hover:bg-emerald-800"
                                    data-testid="recover-step3-submit"
                                >
                                    {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                                    {t.recoverSavePasswordBtn}
                                </Button>
                            </form>
                        )}

                        <p className="mt-4 text-center text-sm">
                            <Link to="/login" className="inline-flex items-center justify-center gap-1 text-neutral-600 hover:text-neutral-900" data-testid="recover-back-link">
                                <ArrowLeft className="h-3.5 w-3.5" />
                                {t.totpBackToLogin}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
